import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { PublicLayout } from '@/components/PublicLayout';
import { api } from '@/utils/api';
import { MapPin, Euro, Calendar, Home, ArrowLeft, CheckCircle2, ZoomIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImageLightbox } from '@/components/ImageLightbox';

const ListingDetail = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    loadListing();
  }, [id]);

  const loadListing = async () => {
    try {
      const response = await api.get('/listings/public');
      const found = response.data.find(l => l.listing_id === id);
      setListing(found);
    } catch (error) {
      console.error('Error loading listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#127abb] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </PublicLayout>
    );
  }

  if (!listing) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Logement non trouvé</h1>
          <Link to="/listings">
            <Button className="bg-[#127abb] hover:bg-[#0e6292]">
              Retour aux annonces
            </Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const hasImages = listing.images && listing.images.length > 0;

  return (
    <PublicLayout>
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <Link to="/listings" className="inline-flex items-center gap-2 text-[#127abb] hover:text-[#0e6292] mb-8 font-medium transition-colors">
            <ArrowLeft size={20} />
            {t.listings.title}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images Gallery */}
            <div>
              {hasImages ? (
                <div className="space-y-4">
                  {/* Main Image */}
                  <div className="relative h-96 rounded-2xl overflow-hidden shadow-xl bg-slate-200 group">
                    <img
                      src={listing.images[0]}
                      alt={language === 'fr' ? listing.title_fr : listing.title_en}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 cursor-pointer"
                      onClick={() => openLightbox(0)}
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center cursor-pointer"
                      onClick={() => openLightbox(0)}
                    >
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-4">
                        <ZoomIn className="text-[#127abb]" size={32} strokeWidth={2} />
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 bg-white px-4 py-2 rounded-full text-sm font-semibold text-[#127abb] shadow-lg">
                      {listing.type}
                    </div>
                  </div>
                  
                  {/* Thumbnail Gallery */}
                  {listing.images.length > 1 && (
                    <div className="grid grid-cols-4 gap-3">
                      {listing.images.slice(1, 5).map((img, idx) => (
                        <div 
                          key={idx} 
                          className="relative h-24 rounded-lg overflow-hidden bg-slate-200 cursor-pointer group"
                          onClick={() => openLightbox(idx + 1)}
                        >
                          <img
                            src={img}
                            alt={`${language === 'fr' ? listing.title_fr : listing.title_en} ${idx + 2}`}
                            className="w-full h-full object-cover transition-all duration-300 group-hover:scale-110 group-hover:brightness-75"
                          />
                          {/* Hover effect */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                            <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={20} />
                          </div>
                        </div>
                      ))}
                      {/* Show more indicator */}
                      {listing.images.length > 5 && (
                        <div 
                          className="relative h-24 rounded-lg overflow-hidden bg-slate-800 cursor-pointer hover:bg-slate-700 transition-colors flex items-center justify-center"
                          onClick={() => openLightbox(5)}
                        >
                          <div className="text-center text-white">
                            <ZoomIn size={24} className="mx-auto mb-1" />
                            <span className="text-sm font-semibold">+{listing.images.length - 5}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative h-96 rounded-2xl overflow-hidden shadow-xl bg-slate-200">
                  <div className="w-full h-full flex items-center justify-center">
                    <Home className="text-slate-400" size={80} />
                  </div>
                  <div className="absolute top-4 right-4 bg-white px-4 py-2 rounded-full text-sm font-semibold text-[#127abb] shadow-lg">
                    {listing.type}
                  </div>
                </div>
              )}
            </div>

            {/* Details */}
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                {language === 'fr' ? listing.title_fr : listing.title_en}
              </h1>

              <div className="flex items-center gap-2 text-slate-600 mb-6">
                <MapPin size={20} className="text-[#127abb]" />
                <span className="text-lg">{listing.location}</span>
              </div>

              <div className="bg-primary-50 rounded-2xl p-6 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">{t.listings.rent}</p>
                    <p className="text-3xl font-bold text-[#127abb]">{listing.rent}€<span className="text-lg">/mois</span></p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">{t.listings.deposit}</p>
                    <p className="text-2xl font-bold text-slate-900">{listing.deposit}€</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <Calendar size={20} className="text-[#127abb]" />
                  <div>
                    <p className="text-sm text-slate-600">{t.listings.available}</p>
                    <p className="font-semibold text-slate-900">{listing.available_from}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-[#127abb]" />
                  <div>
                    <p className="text-sm text-slate-600">{t.listings.duration}</p>
                    <p className="font-semibold text-slate-900">{listing.duration}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6 mb-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-4">Description</h3>
                <p className="text-slate-600 leading-relaxed">
                  {language === 'fr' ? listing.description_fr : listing.description_en}
                </p>
              </div>

              {listing.features && listing.features.length > 0 && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="font-semibold text-lg text-slate-900 mb-4">{t.listings.features}</h3>
                  <div className="flex flex-wrap gap-2">
                    {listing.features.map((feature, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-slate-200 transition-colors"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8">
                <Link to="/contact">
                  <Button className="w-full bg-[#127abb] hover:bg-[#0e6292] text-white rounded-full py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                    Contactez-nous pour ce logement
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && hasImages && (
        <ImageLightbox
          images={listing.images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </PublicLayout>
  );
};

export default ListingDetail;